import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import type { Role } from '../../shared/types'

const roleLabel: Record<Role, string> = {
  super_admin: 'Resilience admin',
  company_admin: 'Admin',
  company_user: 'User',
}

// Your-account card: identity at a glance, with changing your password as a
// deliberate secondary action rather than a form that's always sitting open.
export function AccountCard() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState(false)

  if (!user) return null

  return (
    <div className="card" style={{ maxWidth: 480 }}>
      <div style={{ fontWeight: 700, marginBottom: 12 }}>Your account</div>

      <Row label="Name" value={user.name} />
      <Row label="Email" value={user.email} />
      <Row label="Role" value={roleLabel[user.role]} />

      {done && !open && <p className="form-ok">Password updated.</p>}

      {!open ? (
        <button
          className="btn"
          style={{ border: '1px solid var(--line)', marginTop: 14 }}
          onClick={() => {
            setDone(false)
            setOpen(true)
          }}
        >
          Change password
        </button>
      ) : (
        <ChangePassword
          onCancel={() => setOpen(false)}
          onDone={() => {
            setOpen(false)
            setDone(true)
          }}
        />
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 14 }}>
      <div style={{ width: 70, color: 'var(--gray)', flexShrink: 0 }}>{label}</div>
      <div style={{ minWidth: 0, wordBreak: 'break-word' }}>{value}</div>
    </div>
  )
}

function ChangePassword({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (next.length < 8) {
      setError('New password must be at least 8 characters.')
      return
    }
    if (next !== confirm) {
      setError('New passwords do not match.')
      return
    }
    setBusy(true)
    try {
      await api.changePassword(current, next)
      onDone()
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_current'
          ? 'Your current password is incorrect.'
          : 'Could not change your password. Please try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ borderTop: '1px solid var(--line)', marginTop: 14, paddingTop: 14 }}
    >
      <div className="field">
        <label>Current password</label>
        <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
      </div>
      <div className="field">
        <label>New password</label>
        <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} minLength={8} required />
      </div>
      <div className="field">
        <label>Confirm new password</label>
        <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
      </div>
      {error && <p className="form-error">{error}</p>}
      <p style={{ fontSize: 12, color: 'var(--gray)', margin: '0 0 12px' }}>
        Your other sessions will be signed out.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Updating…' : 'Update password'}
        </button>
        <button type="button" className="btn" style={{ border: '1px solid var(--line)' }} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}
